import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import SiteCard from '../components/SiteCard';
import SiteModal from '../components/SiteModal';
import GA4ConnectionModal from '../components/GA4ConnectionModal';
import AdSenseConnectionModal from '../components/AdSenseConnectionModal';
import GSCConnectionModal from '../components/GSCConnectionModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import FeedbackNotification from '../components/FeedbackNotification';
import OnboardingBanner from '../components/OnboardingBanner';
import Revvy from '../components/Revvy';
import usePageMetadata from '../utils/usePageMetadata';
import './Dashboard.css';

const PLAN_LIMITS = {
  free: { label: 'Free Plan', maxSites: 1 },
  basic: { label: 'Basic Plan', maxSites: 3 },
  pro: { label: 'Pro Plan', maxSites: 10 },
  ultimate: { label: 'Ultimate Plan', maxSites: 999 },
  enterprise: { label: 'Enterprise', maxSites: null } // null means unlimited
};

const Dashboard = () => {
  usePageMetadata({
    title: 'Sites | The Ad Shop',
    description: 'Manage your websites and connect Google Analytics, AdSense, and Search Console accounts.'
  });

  const { token, user } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [deletingSite, setDeletingSite] = useState(null);
  const [ga4Site, setGa4Site] = useState(null);
  const [ga4PropertiesFromOAuth, setGa4PropertiesFromOAuth] = useState(null);
  const [adsenseSite, setAdsenseSite] = useState(null);
  const [adsenseAccountsFromOAuth, setAdsenseAccountsFromOAuth] = useState(null);
  const [gscSite, setGscSite] = useState(null);
  const [gscSitesFromOAuth, setGscSitesFromOAuth] = useState(null);
  const [error, setError] = useState('');
  const [dismissedOnboardingSteps, setDismissedOnboardingSteps] = useState(() => {
    const stored = localStorage.getItem('dismissedOnboardingSteps');
    return stored ? JSON.parse(stored) : [];
  });

  const fetchSites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/sites', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data.sites);
    } catch (error) {
      console.error('Error fetching sites:', error);
      setError('Failed to load sites. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSites();
    
    // Check for AdSense account selection parameters from OAuth callback
    const processAdsenseAccounts = (siteIdValue, accountsArray, source) => {
      if (!siteIdValue || !Array.isArray(accountsArray) || accountsArray.length === 0) {
        console.warn(`No AdSense accounts to process from ${source}`);
        return false;
      }

      setAdsenseAccountsFromOAuth({ siteId: siteIdValue, accounts: accountsArray });
      console.log('✅ Loaded AdSense accounts from OAuth', {
        siteId: siteIdValue,
        accountCount: accountsArray.length,
        source
      });
      return true;
    };

    const urlParams = new URLSearchParams(window.location.search);
    const siteId = urlParams.get('siteId');
    const properties = urlParams.get('properties');
    const adsenseAccounts = urlParams.get('adsenseAccounts');
    const pendingAdsense = urlParams.get('pendingAdsense');
    const pendingGsc = urlParams.get('pendingGsc');
    let handled = false;

    // Handle GA4 property selection from OAuth callback
    if (siteId && properties) {
      console.log('🔍 GA4 OAuth params detected', { siteId, hasProperties: Boolean(properties) });
      try {
        const decodedProperties = JSON.parse(decodeURIComponent(properties));
        setGa4PropertiesFromOAuth({ siteId, properties: decodedProperties });
        console.log('✅ Loaded GA4 properties from OAuth', {
          siteId,
          propertyCount: decodedProperties.length
        });
        // Clean up URL to avoid repeated processing
        window.history.replaceState({}, document.title, window.location.pathname);
        handled = true;
      } catch (error) {
        console.error('Error parsing GA4 properties from query string:', error);
      }
    }

    if (siteId && adsenseAccounts) {
      console.log('🔍 AdSense OAuth params detected', { siteId, raw: adsenseAccounts });
      try {
        const decoded = decodeURIComponent(adsenseAccounts);
        const parsedAccounts = JSON.parse(decoded);
        handled = processAdsenseAccounts(siteId, parsedAccounts, 'querystring');
      } catch (error) {
        console.error('Error parsing AdSense accounts from query string:', error);
      }

      // Clean up URL to avoid repeated processing
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      console.log('ℹ️ No AdSense accounts in query string', {
        siteIdFromQuery: siteId,
        hasAccountsParam: Boolean(adsenseAccounts),
        pendingAdsense
      });
    }

    if (!handled) {
      const pendingAccounts = localStorage.getItem('pendingAdsenseAccounts');
      const pendingSiteId = localStorage.getItem('pendingAdsenseSiteId');
      const pendingTimestamp = localStorage.getItem('pendingAdsenseTimestamp');
      if (pendingAccounts && pendingSiteId) {
        console.log('📦 Found pending AdSense data in localStorage', {
          siteId: pendingSiteId,
          timestamp: pendingTimestamp,
          hasPendingQueryFlag: Boolean(pendingAdsense)
        });
      }
      if (pendingAccounts && pendingSiteId) {
        try {
          const parsedAccounts = JSON.parse(pendingAccounts);
          if (processAdsenseAccounts(pendingSiteId, parsedAccounts, 'localStorage')) {
            localStorage.removeItem('pendingAdsenseAccounts');
            localStorage.removeItem('pendingAdsenseSiteId');
            localStorage.removeItem('pendingAdsenseTimestamp');
            const url = new URL(window.location.href);
            if (url.searchParams.has('pendingAdsense')) {
              url.searchParams.delete('pendingAdsense');
              window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
            }
          }
        } catch (error) {
          console.error('Error parsing pending AdSense accounts from localStorage:', error);
          localStorage.removeItem('pendingAdsenseAccounts');
          localStorage.removeItem('pendingAdsenseSiteId');
          localStorage.removeItem('pendingAdsenseTimestamp');
        }
      } else if (pendingAdsense) {
        console.warn('⚠️ Pending AdSense flag set but no cached data found');
      }
    }

    const processGscSites = (siteIdValue, sitesArray, source) => {
      if (!siteIdValue || !Array.isArray(sitesArray) || sitesArray.length === 0) {
        console.warn(`No Search Console sites to process from ${source}`);
        return false;
      }

      setGscSitesFromOAuth({ siteId: siteIdValue, sites: sitesArray });
      console.log('✅ Loaded Search Console sites from OAuth', {
        siteId: siteIdValue,
        siteCount: sitesArray.length,
        source
      });
      return true;
    };

    const pendingGscSites = localStorage.getItem('pendingGscSites');
    const pendingGscSiteId = localStorage.getItem('pendingGscSiteId');
    const pendingGscTimestamp = localStorage.getItem('pendingGscTimestamp');

    if (pendingGscSites && pendingGscSiteId) {
      console.log('📦 Found pending Search Console data in localStorage', {
        siteId: pendingGscSiteId,
        timestamp: pendingGscTimestamp,
        hasPendingQueryFlag: Boolean(pendingGsc)
      });
      try {
        const parsedSites = JSON.parse(pendingGscSites);
        processGscSites(pendingGscSiteId, parsedSites, 'localStorage');
      } catch (error) {
        console.error('Error parsing pending Search Console sites from localStorage:', error);
      } finally {
        localStorage.removeItem('pendingGscSites');
        localStorage.removeItem('pendingGscSiteId');
        localStorage.removeItem('pendingGscTimestamp');
        const url = new URL(window.location.href);
        if (url.searchParams.has('pendingGsc')) {
          url.searchParams.delete('pendingGsc');
          window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
        }
      }
    } else if (pendingGsc) {
      console.warn('⚠️ Pending Search Console flag set but no cached data found');
    }
  }, [fetchSites]);

  // Open GA4 modal with properties when sites are loaded and we have OAuth properties
  useEffect(() => {
    if (ga4PropertiesFromOAuth && sites.length > 0 && !ga4Site) {
      const site = sites.find(s => s.siteId === ga4PropertiesFromOAuth.siteId);
      if (site) {
        setGa4Site(site);
        console.log('🪟 Opening GA4 connection modal with OAuth properties', {
          siteId: site.siteId,
          propertyCount: ga4PropertiesFromOAuth.properties.length
        });
      }
    }
  }, [ga4PropertiesFromOAuth, sites, ga4Site]);

  // Open AdSense modal with accounts when sites are loaded and we have OAuth accounts
  useEffect(() => {
    if (adsenseAccountsFromOAuth && sites.length > 0 && !adsenseSite) {
      const site = sites.find(s => s.siteId === adsenseAccountsFromOAuth.siteId);
      if (site) {
        setAdsenseSite(site);
        // Accounts will be passed to modal
        console.log('🪟 Opening AdSense connection modal with OAuth accounts', {
          siteId: site.siteId,
          accountCount: adsenseAccountsFromOAuth.accounts.length
        });
      }
    }
  }, [adsenseAccountsFromOAuth, sites, adsenseSite]);

  useEffect(() => {
    if (gscSitesFromOAuth && sites.length > 0 && !gscSite) {
      const site = sites.find((s) => s.siteId === gscSitesFromOAuth.siteId);
      if (site) {
        setGscSite(site);
        console.log('🪟 Opening Search Console connection modal with OAuth sites', {
          siteId: site.siteId,
          propertyCount: gscSitesFromOAuth.sites.length
        });
      }
    }
  }, [gscSitesFromOAuth, sites, gscSite]);

  const planLimitConfig = useMemo(() => {
    const planKey = (user?.plan || 'free').toLowerCase();
    return PLAN_LIMITS[planKey] || PLAN_LIMITS.free;
  }, [user?.plan]);

  const maxSitesAllowed = planLimitConfig.maxSites;
  // Handle null/undefined (unlimited) and numeric limits
  const isUnlimited = maxSitesAllowed === null || maxSitesAllowed === undefined;
  const maxSitesNumber = isUnlimited ? null : Number(maxSitesAllowed);
  const atSiteLimit = !isUnlimited && Number.isFinite(maxSitesNumber) && maxSitesNumber > 0 && sites.length >= maxSitesNumber;
  const limitDisplay = isUnlimited 
    ? 'unlimited sites' 
    : maxSitesNumber >= 999 
      ? 'an unlimited number of sites' 
      : `${maxSitesNumber} site${maxSitesNumber === 1 ? '' : 's'}`;

  const handleAddSite = async (formData) => {
    if (atSiteLimit) {
      setShowAddModal(false);
      setError(`Your ${planLimitConfig.label} allows up to ${limitDisplay}. Upgrade your plan to add more sites.`);
      return;
    }
    try {
      const response = await axios.post('/api/sites', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newSite = response.data.site;
      setSites([...sites, newSite]);
      setShowAddModal(false);
      setError('');
      
      // If this was the first site, automatically prompt to connect GA4
      if (sites.length === 0) {
        // Small delay to let the UI update
        setTimeout(() => {
          handleConnectAnalytics(newSite);
        }, 500);
      }
    } catch (error) {
      console.error('Error adding site:', error);
      setError(error.response?.data?.message || 'Failed to add site. Please try again.');
    }
  };

  const handleEditSite = (site) => {
    setEditingSite(site);
  };

  const handleUpdateSite = async (formData) => {
    try {
      const response = await axios.put(`/api/sites/${editingSite.siteId}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(sites.map(s => s.siteId === editingSite.siteId ? response.data.site : s));
      setEditingSite(null);
      setError('');
    } catch (error) {
      console.error('Error updating site:', error);
      setError(error.response?.data?.message || 'Failed to update site. Please try again.');
    }
  };

  const handleDeleteClick = (site) => {
    setDeletingSite(site);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/sites/${deletingSite.siteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(sites.filter(s => s.siteId !== deletingSite.siteId));
      setDeletingSite(null);
      setError('');
    } catch (error) {
      console.error('Error deleting site:', error);
      setError(error.response?.data?.message || 'Failed to delete site. Please try again.');
      setDeletingSite(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeletingSite(null);
  };

  const handleConnectAnalytics = (site) => {
    setGa4Site(site);
  };

  const handleGA4Connect = async (property) => {
    try {
      const response = await axios.post(`/api/sites/${ga4Site.siteId}/analytics`, {
        propertyId: property.propertyId,
        propertyName: property.propertyName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update the site in the list
      setSites(sites.map(s => s.siteId === ga4Site.siteId ? response.data.site : s));
      setGa4Site(null);
      setGa4PropertiesFromOAuth(null); // Clear OAuth properties
      setError('');
      
      // Refresh sites to get updated connection status
      fetchSites();
      // User stays on sites page - no redirect needed
    } catch (error) {
      console.error('Error connecting GA4:', error);
      setError(error.response?.data?.message || 'Failed to connect GA4 property');
    }
  };

  const handleConnectAdSense = (site) => {
    setAdsenseSite(site);
  };

  const handleAdSenseConnect = async (connectionData) => {
    try {
      const response = await axios.post(`/api/sites/${adsenseSite.siteId}/adsense`, connectionData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update the site in the list
      setSites(sites.map(s => s.siteId === adsenseSite.siteId ? response.data.site : s));
      setAdsenseSite(null);
      setError('');
      
      // Refresh sites to get updated connection status
      fetchSites();
    } catch (error) {
      console.error('Error connecting AdSense:', error);
      setError(error.response?.data?.message || 'Failed to connect AdSense account');
    }
  };

  const handleConnectSearchConsole = (site) => {
    setGscSite(site);
  };

  const handleGSCConnect = async (connectionData) => {
    if (!gscSite) return;
    try {
      const response = await axios.post(`/api/sites/${gscSite.siteId}/gsc`, connectionData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSites(sites.map((s) => (s.siteId === gscSite.siteId ? response.data.site : s)));
      setGscSite(null);
      setGscSitesFromOAuth(null);
      setError('');
      
      // Refresh sites to get updated connection status
      fetchSites();
    } catch (error) {
      console.error('Error connecting Search Console:', error);
      setError(error.response?.data?.message || 'Failed to connect Search Console property');
    }
  };

  // Determine onboarding step
  const onboardingStep = useMemo(() => {
    // Check if onboarding was dismissed
    if (dismissedOnboardingSteps.includes('all')) return null;
    
    // Step 1: No sites created
    if (sites.length === 0) {
      return dismissedOnboardingSteps.includes('create-site') ? null : 'create-site';
    }
    
    // Step 2: Find first site without GA4
    const siteWithoutGA4 = sites.find(s => !s.analyticsPropertyId);
    if (siteWithoutGA4) {
      return dismissedOnboardingSteps.includes('connect-ga4') ? null : 'connect-ga4';
    }
    
    // Step 3: Optional - suggest AdSense for first site without it
    const siteWithoutAdSense = sites.find(s => !s.adsenseAccountId);
    if (siteWithoutAdSense && !dismissedOnboardingSteps.includes('connect-adsense')) {
      return 'connect-adsense';
    }
    
    return null;
  }, [sites, dismissedOnboardingSteps]);

  const onboardingSite = useMemo(() => {
    if (onboardingStep === 'connect-ga4' || onboardingStep === 'connect-adsense' || onboardingStep === 'connect-gsc') {
      return sites.find(s => 
        (onboardingStep === 'connect-ga4' && !s.analyticsPropertyId) ||
        (onboardingStep === 'connect-adsense' && !s.adsenseAccountId) ||
        (onboardingStep === 'connect-gsc' && !s.gscSiteUrl)
      );
    }
    return null;
  }, [onboardingStep, sites]);

  const handleOnboardingAction = () => {
    switch (onboardingStep) {
      case 'create-site':
        setShowAddModal(true);
        break;
      case 'connect-ga4':
        if (onboardingSite) {
          handleConnectAnalytics(onboardingSite);
        }
        break;
      case 'connect-adsense':
        if (onboardingSite) {
          handleConnectAdSense(onboardingSite);
        }
        break;
      case 'connect-gsc':
        if (onboardingSite) {
          handleConnectSearchConsole(onboardingSite);
        }
        break;
      default:
        break;
    }
  };

  const handleDismissOnboarding = () => {
    const updated = [...dismissedOnboardingSteps, onboardingStep];
    setDismissedOnboardingSteps(updated);
    localStorage.setItem('dismissedOnboardingSteps', JSON.stringify(updated));
  };

  // Determine Revvy state based on user progress
  const getRevvyState = useMemo(() => {
    if (!user) return null;
    
    // New user with no sites
    if (sites.length === 0) {
      return 'onboarding_start';
    }
    
    // User has sites but no GA4 connected
    const siteWithoutGA4 = sites.find(s => !s.analyticsPropertyId);
    if (siteWithoutGA4) {
      return 'initial_data_validation';
    }
    
    // User has GA4 but no AdSense
    const siteWithoutAdSense = sites.find(s => !s.adsenseAccountId);
    if (siteWithoutAdSense) {
      return 'connect_monetization_stack';
    }
    
    // User has everything connected
    return 'revenue_normalization_running';
  }, [user, sites]);

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your sites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <main className="dashboard-content">
        <div className="dashboard-header">
          <div className="header-content">
            <h1>Your Sites</h1>
            <p>Manage your websites and connect them to analytics and ad platforms</p>
          </div>
          <button 
            className="btn-add-site"
            onClick={() => setShowAddModal(true)}
            disabled={atSiteLimit || user?.email === 'demo@example.com'}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Site
          </button>
        </div>

        {atSiteLimit && (
          <p className="plan-limit-message">
            Your {planLimitConfig.label} allows up to {limitDisplay}. Upgrade your plan to manage more sites.
          </p>
        )}

        {error && (
          <div className="error-banner">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Revvy Component - Show for onboarding */}
        {getRevvyState && (
          <Revvy 
            initialStateId={getRevvyState}
            onStateChange={(newState) => {
              // Handle state changes if needed
            }}
          />
        )}

        {onboardingStep && (
          <OnboardingBanner
            step={onboardingStep}
            site={onboardingSite}
            onAction={handleOnboardingAction}
            onDismiss={handleDismissOnboarding}
          />
        )}

        {sites.length === 0 ? (
          <div className="empty-state">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="10" y="10" width="60" height="60" rx="8" />
              <path d="M25 25h30v30H25z" />
            </svg>
            <h2>No sites yet</h2>
            <p>Add your first website to get started with analytics and ad management</p>
            <button 
              className="btn-primary"
              onClick={() => setShowAddModal(true)}
              disabled={atSiteLimit || user?.email === 'demo@example.com'}
            >
              Add Your First Site
            </button>
          </div>
        ) : (
          <div className="sites-grid">
            {sites.map(site => (
              <SiteCard
                key={site.siteId}
                site={site}
                onEdit={handleEditSite}
                onDelete={handleDeleteClick}
                onConnectAnalytics={handleConnectAnalytics}
                onConnectAdSense={handleConnectAdSense}
                onConnectGSC={handleConnectSearchConsole}
              />
            ))}
          </div>
        )}
      </main>

      <SiteModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddSite}
      />

      <SiteModal
        isOpen={!!editingSite}
        onClose={() => setEditingSite(null)}
        onSave={handleUpdateSite}
        site={editingSite}
      />

      <GA4ConnectionModal
        isOpen={!!ga4Site}
        onClose={() => {
          setGa4Site(null);
          setGa4PropertiesFromOAuth(null);
        }}
        onConnect={handleGA4Connect}
        site={ga4Site}
        initialProperties={
          ga4PropertiesFromOAuth && ga4Site && ga4PropertiesFromOAuth.siteId === ga4Site.siteId
            ? ga4PropertiesFromOAuth.properties
            : null
        }
      />

      <AdSenseConnectionModal
        isOpen={!!adsenseSite}
        onClose={() => {
          setAdsenseSite(null);
          setAdsenseAccountsFromOAuth(null);
        }}
        onConnect={handleAdSenseConnect}
        site={adsenseSite}
        initialAccounts={adsenseAccountsFromOAuth?.accounts || null}
      />

      <GSCConnectionModal
        isOpen={!!gscSite}
        onClose={() => {
          setGscSite(null);
          setGscSitesFromOAuth(null);
        }}
        onConnect={handleGSCConnect}
        site={gscSite}
        initialSites={
          gscSitesFromOAuth && gscSite && gscSitesFromOAuth.siteId === gscSite.siteId
            ? gscSitesFromOAuth.sites
            : null
        }
      />

      <DeleteConfirmationModal
        site={deletingSite}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
      <FeedbackNotification />
    </div>
  );
};

export default Dashboard;
